/**
 * The DocumentListDownloadMenu component handles displaying options for downloading document details
 * for all documents in the list
 */
import React, { ReactElement } from "react";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/GetApp";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { GridSelectionModel } from "@mui/x-data-grid";

import { IVaultViewAllLoansDocumentDetailData } from "../VaultInterfaces";
import { DocumentOptions } from "./ListContextMenu";
import documentListService from "./documentListService";

interface IListPrintMenuProps {
  canDownload: boolean;
  checkedRows: GridSelectionModel;
  documentListData: IVaultViewAllLoansDocumentDetailData[];
  onDownload(option: DocumentOptions): void;
  workflowType?: string;
}

export default function DocumentListDownloadMenu(props: IListPrintMenuProps): ReactElement {
  const { canDownload, checkedRows, documentListData, onDownload, workflowType } = props;
  const [subMenuOpen, setSubMenuOpen] = React.useState(false);
  const auditLogDisabled = workflowType === "MERS";
  const menuDisabled = !canDownload || auditLogDisabled;

  function handleSubMenuClick() {
    setSubMenuOpen(!subMenuOpen);
  }

  return (
    <>
      <ListItem button key="export" disabled={menuDisabled} onClick={handleSubMenuClick} dense>
        <ListItemIcon>
          <DownloadIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Download</ListItemText>
        {subMenuOpen ? (
          <ExpandLessIcon sx={{ ml: 2 }} fontSize="small" />
        ) : (
          <ExpandMoreIcon sx={{ ml: 2 }} fontSize="small" />
        )}
      </ListItem>
      <Collapse in={subMenuOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          <ListItem
            button
            dense
            disabled={documentListService.shouldDisableOption(documentListData, DocumentOptions.All)}
            onClick={() => onDownload(DocumentOptions.All)}
            sx={{ pl: 4 }}
          >
            <ListItemText>All</ListItemText>
          </ListItem>
          <ListItem
            button
            dense
            disabled={documentListService.shouldDisableOption(documentListData, DocumentOptions.Selected, checkedRows)}
            onClick={() => onDownload(DocumentOptions.Selected)}
            sx={{ pl: 4 }}
          >
            <ListItemText>Selected</ListItemText>
          </ListItem>
          <ListItem
            button
            dense
            disabled={documentListService.shouldDisableOption(documentListData, DocumentOptions.InkSign)}
            onClick={() => onDownload(DocumentOptions.InkSign)}
            sx={{ pl: 4 }}
          >
            <ListItemText>Ink</ListItemText>
          </ListItem>
          <ListItem
            button
            dense
            disabled={documentListService.shouldDisableOption(documentListData, DocumentOptions.eSign)}
            onClick={() => onDownload(DocumentOptions.eSign)}
            sx={{ pl: 4 }}
          >
            <ListItemText>eSign</ListItemText>
          </ListItem>
        </List>
      </Collapse>
    </>
  );
}
